/**
 * SectionManager Component
 *
 * Manages the modular content sections for a single theme template.
 * Allows users to:
 *  - Toggle sections on or off
 *  - Reorder sections with Up/Down arrow buttons
 *  - Choose a style/layout variant for each enabled section
 *
 * @param {string}   templateId     - Template ID (e.g. 'home', 'about')
 * @param {string}   industry       - Industry slug for filtering industry-specific sections
 * @param {Array}    sections       - Current sections state: [{ sectionId, enabled, order, variant }]
 * @param {function} onChange       - Callback called with the updated sections array
 */

import React from 'react';
import {
  Box,
  Typography,
  Switch,
  Chip,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Divider
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// ---------------------------------------------------------------------------
// Inline section definitions (mirrors sectionLibrary.js in the backend).
// Kept in sync manually; the canonical source is the backend config.
// ---------------------------------------------------------------------------

const SECTION_DEFS = {
  hero: {
    id: 'hero',
    name: 'Hero / Banner',
    icon: '🦸',
    description: 'Main header with headline and call-to-action buttons',
    variants: [
      { id: 'centered', name: 'Centered' },
      { id: 'left-aligned', name: 'Left Aligned' },
      { id: 'fullscreen', name: 'Fullscreen' },
      { id: 'video', name: 'Video Background' }
    ]
  },
  features: {
    id: 'features',
    name: 'Features / Services',
    icon: '⚡',
    description: 'Showcase key services, features, or benefits',
    variants: [
      { id: 'card-grid', name: 'Card Grid' },
      { id: 'icon-list', name: 'Icon List' },
      { id: 'two-column', name: 'Two Column' }
    ]
  },
  testimonials: {
    id: 'testimonials',
    name: 'Testimonials',
    icon: '⭐',
    description: 'Client reviews and social proof',
    variants: [
      { id: 'card-grid', name: 'Card Grid' },
      { id: 'carousel', name: 'Carousel' },
      { id: 'quote-large', name: 'Large Quote' },
      { id: 'dark-strip', name: 'Dark Strip' }
    ]
  },
  gallery: {
    id: 'gallery',
    name: 'Gallery / Portfolio',
    icon: '🖼️',
    description: 'Visual showcase of work, projects, or photos',
    variants: [
      { id: 'uniform-grid', name: 'Uniform Grid' },
      { id: 'masonry', name: 'Masonry' },
      { id: 'slideshow', name: 'Slideshow' }
    ]
  },
  cta: {
    id: 'cta',
    name: 'Call to Action',
    icon: '📣',
    description: 'Prompt visitors to take the next step',
    variants: [
      { id: 'banner', name: 'Colored Banner' },
      { id: 'split', name: 'Split Layout' },
      { id: 'centered', name: 'Centered' }
    ]
  },
  contact_cta: {
    id: 'contact_cta',
    name: 'Contact Strip',
    icon: '📬',
    description: 'Quick contact info or mini contact form',
    variants: [
      { id: 'card-strip', name: 'Info Strip' },
      { id: 'simple-form', name: 'Simple Form' },
      { id: 'map-form', name: 'Map + Form' }
    ]
  },
  stats: {
    id: 'stats',
    name: 'Stats / Numbers',
    icon: '📊',
    description: 'Highlight impressive numbers and achievements',
    variants: [
      { id: 'horizontal', name: 'Horizontal Strip' },
      { id: 'grid', name: 'Grid with Icons' }
    ]
  },
  calculator: {
    id: 'calculator',
    name: 'Mortgage Calculator',
    icon: '🧮',
    description: 'Interactive mortgage payment estimator',
    industryRelevant: ['real-estate'],
    variants: [
      { id: 'full-width', name: 'Full Width' },
      { id: 'sidebar', name: 'Sidebar Widget' },
      { id: 'modal-trigger', name: 'Popup Button' }
    ]
  },
  agents: {
    id: 'agents',
    name: 'Agent / Team Directory',
    icon: '👥',
    description: 'Show your team or real estate agents with bios',
    industryRelevant: ['real-estate'],
    variants: [
      { id: 'card-grid', name: 'Card Grid' },
      { id: 'list', name: 'Compact List' }
    ]
  },
  listings: {
    id: 'listings',
    name: 'Property Listings',
    icon: '🏠',
    description: 'Featured property cards or search results',
    industryRelevant: ['real-estate'],
    variants: [
      { id: 'card-grid', name: 'Card Grid' },
      { id: 'map-sidebar', name: 'Map + Sidebar' }
    ]
  }
};

const SectionManager = ({ templateId, industry, sections = [], onChange }) => {
  // Sort sections by their current order field for stable display
  const sortedSections = [...sections].sort((a, b) => a.order - b.order);

  const handleToggle = (sectionId) => {
    const updated = sections.map((s) =>
      s.sectionId === sectionId ? { ...s, enabled: !s.enabled } : s
    );
    onChange(updated);
  };

  const handleVariantChange = (sectionId, variant) => {
    const updated = sections.map((s) =>
      s.sectionId === sectionId ? { ...s, variant } : s
    );
    onChange(updated);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const reordered = [...sortedSections];
    // Swap order values with the item above
    const prevOrder = reordered[index - 1].order;
    const currOrder = reordered[index].order;
    reordered[index - 1] = { ...reordered[index - 1], order: currOrder };
    reordered[index] = { ...reordered[index], order: prevOrder };
    onChange(reordered);
  };

  const handleMoveDown = (index) => {
    if (index === sortedSections.length - 1) return;
    const reordered = [...sortedSections];
    const nextOrder = reordered[index + 1].order;
    const currOrder = reordered[index].order;
    reordered[index + 1] = { ...reordered[index + 1], order: currOrder };
    reordered[index] = { ...reordered[index], order: nextOrder };
    onChange(reordered);
  };

  if (sortedSections.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, fontStyle: 'italic' }}>
        No configurable sections for this template.
      </Typography>
    );
  }

  const enabledCount = sortedSections.filter((s) => s.enabled).length;

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Typography variant="subtitle2" color="text.secondary">
          Sections — use arrows to reorder, toggle to show/hide
        </Typography>
        <Chip
          label={`${enabledCount} of ${sortedSections.length} active`}
          size="small"
          color={enabledCount > 0 ? 'primary' : 'default'}
          variant="outlined"
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {sortedSections.map((section, index) => {
          const def = SECTION_DEFS[section.sectionId];
          if (!def) return null;

          const isIndustrySpecific = Boolean(def.industryRelevant);
          const isFirst = index === 0;
          const isLast = index === sortedSections.length - 1;

          return (
            <Paper
              key={section.sectionId}
              variant="outlined"
              sx={{
                p: 1.5,
                borderColor: section.enabled ? 'primary.main' : 'divider',
                borderWidth: section.enabled ? 2 : 1,
                bgcolor: section.enabled ? 'primary.50' : 'background.paper',
                transition: 'border-color 0.15s, background-color 0.15s',
                opacity: section.enabled ? 1 : 0.65
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                {/* Reorder arrows */}
                <Box display="flex" flexDirection="column" sx={{ flexShrink: 0 }}>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveUp(index)}
                        disabled={isFirst}
                        sx={{ p: 0.25 }}
                      >
                        <ArrowUpwardIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveDown(index)}
                        disabled={isLast}
                        sx={{ p: 0.25 }}
                      >
                        <ArrowDownwardIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                {/* Icon */}
                <Typography variant="h6" component="span" sx={{ flexShrink: 0, lineHeight: 1 }}>
                  {def.icon}
                </Typography>

                {/* Name + description */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {def.name}
                    </Typography>
                    {isIndustrySpecific && (
                      <Chip
                        label="Industry"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ fontSize: '0.6rem', height: 16 }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" noWrap>
                    {def.description}
                  </Typography>
                </Box>

                {/* Variant selector — only when enabled */}
                {section.enabled && def.variants && def.variants.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 130, flexShrink: 0 }}>
                    <InputLabel sx={{ fontSize: '0.75rem' }}>Style</InputLabel>
                    <Select
                      value={section.variant || def.variants[0].id}
                      label="Style"
                      onChange={(e) => handleVariantChange(section.sectionId, e.target.value)}
                      sx={{ fontSize: '0.8rem' }}
                    >
                      {def.variants.map((v) => (
                        <MenuItem key={v.id} value={v.id} sx={{ fontSize: '0.8rem' }}>
                          {v.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Toggle switch */}
                <Tooltip title={section.enabled ? 'Disable section' : 'Enable section'}>
                  <Switch
                    checked={section.enabled}
                    onChange={() => handleToggle(section.sectionId)}
                    size="small"
                    color="primary"
                  />
                </Tooltip>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {enabledCount === 0 && (
        <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
          ⚠️ No sections are active. Enable at least one section for this template.
        </Typography>
      )}
    </Box>
  );
};

// Export the section definitions so TemplateCustomizer can import them
export { SECTION_DEFS };
export default SectionManager;
